import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TopBar } from "../../components/TopBar";
import { PageHeader } from "../../components/PageHeader";
import { Segmented, Select, ChipMulti } from "../../components/Controls";
import { useProjects } from "../../context/ProjectContext";
import { useToast } from "../../context/ToastContext";
import { api } from "../../api/client";
import { defaultConfig } from "../../api/defaults";
import { BrandingEditor } from "../../components/BrandingEditor";
import type {
  Aggregation,
  ArchiveKind,
  DisplayAs,
  LeaderboardConfig,
  ResetInterval,
  ScoreType,
  SortOrder,
  Tiebreaker,
  UpdateMethod,
  Visibility,
  WriteAuth,
} from "../../api/types";

/** Derive a board id slug from a human-readable name. */
function nameToSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function BoardEditor() {
  const { boardId } = useParams();
  const isEdit = Boolean(boardId);
  const { active } = useProjects();
  const pid = active?.id ?? "";
  const toast = useToast();
  const nav = useNavigate();

  const [cfg, setCfg] = useState<LeaderboardConfig>(() => defaultConfig());
  const [boardName, setBoardName] = useState("");
  const [idOverridden, setIdOverridden] = useState(false);
  const [advancedId, setAdvancedId] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  // Phase 11.4: selecting windows turns this into a board group (one child board per window).
  const [windows, setWindows] = useState<string[]>([]);
  const isGroup = windows.length > 0;

  useEffect(() => {
    if (isEdit && boardId && pid) {
      api.getBoard(pid, boardId)
        .then((b) => { setCfg(b); setBoardName(b.id); })
        .catch((e) => toast((e as Error).message, "err"))
        .finally(() => setLoading(false));
    }
  }, [isEdit, boardId, pid, toast]);

  function set<K extends keyof LeaderboardConfig>(key: K, val: LeaderboardConfig[K]) {
    setCfg((c) => ({ ...c, [key]: val }));
  }

  // Gap 3: update one field of the (optional) anti-cheat config; `null` clears it.
  function setAc<K extends keyof NonNullable<LeaderboardConfig["anticheat"]>>(
    key: K,
    val: NonNullable<LeaderboardConfig["anticheat"]>[K] | null,
  ) {
    setCfg((c) => {
      const next = { ...(c.anticheat ?? {}) } as NonNullable<LeaderboardConfig["anticheat"]>;
      if (val === null) delete next[key];
      else next[key] = val;
      return { ...c, anticheat: next };
    });
  }

  function handleNameChange(name: string) {
    setBoardName(name);
    // Persist the human-readable name, and (unless the slug was manually overridden) keep the
    // derived id in sync with it.
    setCfg((c) => ({ ...c, name, ...(idOverridden ? {} : { id: nameToSlug(name) }) }));
  }

  function handleIdChange(id: string) {
    setIdOverridden(true);
    set("id", id);
  }

  const idValid = useMemo(() => /^[a-z0-9][a-z0-9-_]{1,63}$/.test(cfg.id), [cfg.id]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!isEdit && !idValid) {
      toast("Board id must be lowercase alphanumeric with - or _ (2–64 chars).", "err");
      return;
    }
    setBusy(true);
    try {
      if (isEdit && boardId) {
        // PATCH only mutable fields (spec 03: archive_policy, rate_limit, …).
        await api.updateBoard(pid, boardId, {
          archive_policy: cfg.archive_policy,
          rate_limit: cfg.rate_limit,
          visibility: cfg.visibility,
          max_entries: cfg.max_entries,
          display_as: cfg.display_as,
          entry_metadata_schema: cfg.entry_metadata_schema,
          branding: cfg.branding ?? {},
          anticheat: cfg.anticheat ?? {},
        });
        toast(`Board "${boardId}" updated`);
        nav(`/app/boards/${encodeURIComponent(boardId)}`);
      } else {
        // A group sends `windows` (each window sets its own cadence, so the parent doesn't reset).
        const payload = isGroup ? { ...normalize(cfg), reset: null, windows } : normalize(cfg);
        const created = await api.createBoard(pid, payload);
        toast(isGroup ? `Group "${created.id}" created (${windows.length} windows)` : `Board "${created.id}" created`);
        nav(`/app/boards/${encodeURIComponent(created.id)}`);
      }
    } catch (ex) {
      toast((ex as Error).message, "err");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <>
        <TopBar crumbs={<b>Loading…</b>} />
        <div className="content"><div className="card" style={{ padding: 30 }}><div className="skel" style={{ height: 200 }} /></div></div>
      </>
    );
  }

  const lockMsg = "Fixed once the board has scores — these change how players are ranked.";

  return (
    <>
      <TopBar crumbs={<><Link to="/app/boards" className="muted">Leaderboards</Link> / <b>{isEdit ? `Edit ${boardId}` : "New board"}</b></>} />
      <div className="content">
        <PageHeader
          title={isEdit ? "Configure leaderboard" : "Create a leaderboard"}
          intent={isEdit ? "Only mutable settings can change once entries exist." : "Every option has a sensible default — just give your board a name."}
        />

        <form onSubmit={submit}>
          {/* IDENTITY */}
          <section className="card form-section">
            <div className="sec-title"><h3>Identity</h3></div>
            <p className="sec-desc">Give your board a name. That's all you need to get started.</p>
            <div className="form-grid">
              {!isEdit && (
                <div className="field full">
                  <label htmlFor="name">Name <span className="faint">— required</span></label>
                  <input
                    id="name"
                    className="input"
                    placeholder="Weekly High Scores"
                    value={boardName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    required={!isEdit}
                  />
                </div>
              )}
              {isEdit && (
                <div className="field full">
                  <label htmlFor="id-edit">API id</label>
                  <input id="id-edit" className="input mono" value={cfg.id} disabled />
                  <span className="hint">The id your game uses to read &amp; write this board over the API.</span>
                </div>
              )}
              {!isEdit && (
                <div className="field full">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ alignSelf: "flex-start", fontSize: 12 }}
                    onClick={() => setAdvancedId((v) => !v)}
                  >
                    {advancedId ? "▲ Hide advanced" : "▼ Advanced: set the API id"}
                  </button>
                  {advancedId && (
                    <div style={{ marginTop: 10 }}>
                      <input
                        id="id"
                        className="input mono"
                        placeholder="weekly-high-scores"
                        value={cfg.id}
                        onChange={(e) => handleIdChange(e.target.value)}
                      />
                      <span className="hint" style={{ color: cfg.id && !idValid ? "var(--coral)" : undefined }}>
                        The id your game uses to call this board. Defaults from the name; fixed once created.
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* RANKING SEMANTICS */}
          <section className="card form-section">
            <div className="sec-title"><h3>Ranking semantics</h3></div>
            <p className="sec-desc">{lockMsg}</p>
            <div className="form-grid">
              <div className="field">
                <label>Ranking</label>
                <Segmented<SortOrder>
                  value={cfg.sort_order}
                  disabled={isEdit}
                  onChange={(v) => set("sort_order", v)}
                  options={[
                    { value: "desc", label: "Highest wins" },
                    { value: "asc", label: "Lowest wins" },
                  ]}
                />
                <span className="hint">Highest wins for high scores. Lowest wins for speedruns / lap times.</span>
              </div>
              <div className="field">
                <label>Scores are</label>
                <Segmented<ScoreType>
                  value={cfg.score_type}
                  disabled={isEdit}
                  onChange={(v) => set("score_type", v)}
                  options={[
                    { value: "i64", label: "Whole numbers" },
                    { value: "f64", label: "Decimals" },
                  ]}
                />
                <span className="hint">{cfg.score_type === "f64" ? "Use for times, distances, or anything fractional." : "Points, kills, coins — covers most games."}</span>
              </div>
              <div className="field">
                <label htmlFor="display">Display hint (cosmetic)</label>
                <Select<DisplayAs>
                  id="display"
                  value={cfg.display_as}
                  onChange={(v) => set("display_as", v)}
                  options={[
                    { value: "numeric", label: "Numeric" },
                    { value: "seconds", label: "Seconds" },
                    { value: "milliseconds", label: "Milliseconds" },
                    { value: "time_hms", label: "Time (h:m:s)" },
                    { value: "percentage", label: "Percentage" },
                    { value: "currency", label: "Currency" },
                  ]}
                />
                <span className="hint">How clients render the score. Does not affect ranking.</span>
              </div>
              <div className="field">
                <label htmlFor="vis">Visibility</label>
                <Select<Visibility>
                  id="vis"
                  value={cfg.visibility}
                  onChange={(v) => set("visibility", v)}
                  options={[
                    { value: "public", label: "Public — readable by anyone" },
                    { value: "private", label: "Private — auth required to read" },
                  ]}
                />
              </div>
            </div>
          </section>

          {/* UPDATE / AGGREGATION */}
          <section className="card form-section">
            <div className="sec-title"><h3>Update behaviour</h3></div>
            <p className="sec-desc">How a newly submitted score reconciles with the player's stored score.</p>
            <div className="form-grid">
              <div className="field full">
                <label>Update method</label>
                <Segmented<UpdateMethod>
                  value={cfg.update_method}
                  onChange={(v) => set("update_method", v)}
                  options={[
                    { value: "keep_best", label: "Keep best" },
                    { value: "force", label: "Force (last)" },
                    { value: "increment", label: "Increment" },
                    { value: "aggregate", label: "Aggregate" },
                  ]}
                />
                <span className="hint">
                  {cfg.update_method === "keep_best" && "Keep the better value (max for desc, min for asc). Most common."}
                  {cfg.update_method === "force" && "Always overwrite with the latest submitted value."}
                  {cfg.update_method === "increment" && "Add the submitted delta to the running total (cumulative coins/points)."}
                  {cfg.update_method === "aggregate" && "Apply an explicit aggregation function (below)."}
                </span>
              </div>
              {cfg.update_method === "aggregate" && (
                <div className="field">
                  <label htmlFor="agg">Aggregation function</label>
                  <Select<Aggregation>
                    id="agg"
                    value={cfg.aggregation ?? "max"}
                    onChange={(v) => set("aggregation", v)}
                    options={[
                      { value: "last", label: "Last" },
                      { value: "min", label: "Min" },
                      { value: "max", label: "Max" },
                      { value: "sum", label: "Sum" },
                      { value: "count", label: "Count" },
                      { value: "mean", label: "Mean" },
                    ]}
                  />
                </div>
              )}
            </div>
          </section>

          {/* TIME WINDOWS (board group) */}
          {!isEdit && (
            <section className="card form-section">
              <div className="sec-title"><h3>Time windows <span className="faint">— optional</span></h3></div>
              <p className="sec-desc">
                Expose this leaderboard as several time views from a single submit. Pick one or more
                windows and a child board is created per window (read at <code>{cfg.id || "id"}:&lt;window&gt;</code>).
                Leave empty for a single board.
              </p>
              <ChipMulti<string>
                selected={windows}
                onChange={setWindows}
                options={[
                  { value: "all_time", label: "All-time" },
                  { value: "daily", label: "Daily" },
                  { value: "weekly", label: "Weekly" },
                  { value: "monthly", label: "Monthly" },
                  { value: "rolling_24h", label: "Rolling 24h" },
                  { value: "rolling_7d", label: "Rolling 7d" },
                  { value: "rolling_30d", label: "Rolling 30d" },
                ]}
              />
              {isGroup && (
                <p className="hint" style={{ marginTop: 12 }}>
                  Group mode — the <b>Reset &amp; seasons</b> section below doesn't apply; each window sets its own cadence.
                </p>
              )}
            </section>
          )}

          {/* TIEBREAKERS */}
          <section className="card form-section">
            <div className="sec-title"><h3>Tiebreakers</h3></div>
            <p className="sec-desc">When two players have the same score, these decide who ranks higher, in priority order. Click to add; order shown on each chip.</p>
            <ChipMulti<Tiebreaker>
              ordered
              selected={cfg.tiebreaker.filter((t) => t !== "lexicographic_id")}
              onChange={(v) => set("tiebreaker", v.length ? v : ["earliest_submission"])}
              options={[
                { value: "earliest_submission", label: "Earliest submission" },
                { value: "latest_submission", label: "Latest submission" },
                { value: "subscore", label: "Subscore" },
              ]}
            />
            <p className="hint" style={{ marginTop: 12 }}>
              Ranks are always stable and deterministic, even when every tiebreaker above is equal.
            </p>
          </section>

          {/* RESET / SEASONS — not shown for a group (per-window cadence comes from the windows) */}
          {!isGroup && (
          <section className="card form-section">
            <div className="sec-title"><h3>Reset &amp; seasons</h3></div>
            <p className="sec-desc">On reset the board is archived to an immutable version and a fresh one begins. Old seasons stay queryable.</p>
            <div className="form-grid">
              <div className="field">
                <label>Reset</label>
                <Segmented<"never" | "interval">
                  value={cfg.reset ? "interval" : "never"}
                  onChange={(v) => set("reset", v === "never" ? null : { interval: "weekly", reset_tz: "UTC" })}
                  options={[
                    { value: "never", label: "Never (all-time)" },
                    { value: "interval", label: "Recurring" },
                  ]}
                />
              </div>
              {cfg.reset && (
                <>
                  <div className="field">
                    <label htmlFor="interval">Interval</label>
                    <Select<ResetInterval>
                      id="interval"
                      value={cfg.reset.interval}
                      onChange={(v) => set("reset", { ...cfg.reset!, interval: v })}
                      options={[
                        { value: "daily", label: "Daily" },
                        { value: "weekly", label: "Weekly" },
                        { value: "monthly", label: "Monthly" },
                        { value: "cron", label: "CRON expression" },
                      ]}
                    />
                  </div>
                  {cfg.reset.interval === "cron" && (
                    <div className="field">
                      <label htmlFor="cron">CRON</label>
                      <input id="cron" className="input mono" placeholder="0 0 * * 1"
                        value={cfg.reset.cron ?? ""} onChange={(e) => set("reset", { ...cfg.reset!, cron: e.target.value })} />
                    </div>
                  )}
                  <div className="field">
                    <label htmlFor="tz">Timezone</label>
                    <input id="tz" className="input mono" placeholder="UTC"
                      value={cfg.reset.reset_tz ?? "UTC"} onChange={(e) => set("reset", { ...cfg.reset!, reset_tz: e.target.value })} />
                    <span className="hint">IANA tz, e.g. America/Los_Angeles.</span>
                  </div>
                  <div className="field">
                    <label htmlFor="arch">Archive policy</label>
                    <Select<ArchiveKind>
                      id="arch"
                      value={cfg.archive_policy.kind}
                      onChange={(v) => set("archive_policy", { kind: v, keep_last_n: cfg.archive_policy.keep_last_n, ttl_days: cfg.archive_policy.ttl_days })}
                      options={[
                        { value: "keep_all", label: "Keep all versions" },
                        { value: "keep_last_n", label: "Keep last N" },
                        { value: "ttl_days", label: "TTL (days)" },
                      ]}
                    />
                  </div>
                  {cfg.archive_policy.kind === "keep_last_n" && (
                    <div className="field">
                      <label htmlFor="kn">Keep last N versions</label>
                      <input id="kn" className="input mono" type="number" min={1}
                        value={cfg.archive_policy.keep_last_n ?? 12}
                        onChange={(e) => set("archive_policy", { ...cfg.archive_policy, keep_last_n: Number(e.target.value) })} />
                    </div>
                  )}
                  {cfg.archive_policy.kind === "ttl_days" && (
                    <div className="field">
                      <label htmlFor="ttl">TTL (days)</label>
                      <input id="ttl" className="input mono" type="number" min={1}
                        value={cfg.archive_policy.ttl_days ?? 90}
                        onChange={(e) => set("archive_policy", { ...cfg.archive_policy, ttl_days: Number(e.target.value) })} />
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
          )}

          {/* SIZING / ACCESS */}
          <section className="card form-section">
            <div className="sec-title"><h3>Membership, limits &amp; access</h3></div>
            <p className="sec-desc">Bound memory by construction and control who may write.</p>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="max">Max entries</label>
                <input id="max" className="input mono" type="number" min={1} placeholder="unbounded"
                  value={cfg.max_entries ?? ""} onChange={(e) => set("max_entries", e.target.value ? Number(e.target.value) : null)} />
                <span className="hint">Keeps only top-N (bottom-N for asc); cold tail is evicted.</span>
              </div>
              <div className="field">
                <label htmlFor="rl">Rate limit (subs/player/min)</label>
                <input id="rl" className="input mono" type="number" min={0} placeholder="unlimited"
                  value={cfg.rate_limit ?? ""} onChange={(e) => set("rate_limit", e.target.value ? Number(e.target.value) : null)} />
              </div>
              <div className="field full">
                <label>Write auth</label>
                <Segmented<WriteAuth>
                  value={cfg.write_auth}
                  onChange={(v) => set("write_auth", v)}
                  options={[
                    { value: "server_only", label: "Server only (trusted backend)" },
                    { value: "client_signed", label: "Client-signed tokens" },
                  ]}
                />
                <span className="hint">"Server only" = scores come from your backend (most secure). "Client tokens" lets players submit directly from the game.</span>
              </div>
              <div className="field full">
                <label htmlFor="meta">Entry metadata keys (comma-separated, optional)</label>
                <input id="meta" className="input mono" placeholder="display_name, country, avatar_url"
                  value={cfg.entry_metadata_schema ? Object.keys(cfg.entry_metadata_schema).join(", ") : ""}
                  onChange={(e) => {
                    const keys = e.target.value.split(",").map((k) => k.trim()).filter(Boolean);
                    set("entry_metadata_schema", keys.length ? Object.fromEntries(keys.map((k) => [k, "string"])) : null);
                  }} />
                <span className="hint">Opaque blob returned with rank queries so clients need no second lookup.</span>
              </div>
            </div>
          </section>

          {/* PUBLIC PAGE & BRANDING (Gap 1) */}
          <section className="card form-section">
            <div className="sec-title"><h3>Public page &amp; branding</h3></div>
            <p className="sec-desc">
              {cfg.visibility === "public" ? (
                <>
                  This board has a live, embeddable public page at <code>/p/{active?.slug ?? "project"}/{cfg.id || "id"}</code>.
                  {isEdit && (
                    <> <a href={`/p/${active?.slug ?? ""}/${encodeURIComponent(cfg.id)}`} target="_blank" rel="noopener">Open it ↗</a></>
                  )}
                  {" "}Brand it below — these settings override your <b>project default</b> branding, field by field. Leave a field blank to inherit.
                </>
              ) : (
                <>Set this board's <b>Visibility</b> to <b>Public</b> (above) to get a shareable, embeddable public page. You can still configure its branding now — it applies once the board is public.</>
              )}
            </p>
            <BrandingEditor
              value={cfg.branding ?? {}}
              onChange={(b) => set("branding", b)}
              inherited={active?.branding}
              inheritedLabel="project default"
            />
          </section>

          {/* ANTI-CHEAT & MODERATION (Gap 3) */}
          <section className="card form-section">
            <div className="sec-title"><h3>Anti-cheat &amp; moderation</h3></div>
            <p className="sec-desc">
              Suspicious submissions are flagged by the anomaly checks below and reviewed in{" "}
              <Link to="/app/moderation" className="muted">Moderation</Link>. By default a flagged score still
              counts; turn on <b>Hold for review</b> to keep flagged scores <i>out</i> of the leaderboard until
              you approve them. All optional; leave blank to disable.
            </p>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="ac-std">Outlier threshold (σ)</label>
                <input id="ac-std" className="input mono" type="number" min={0} step={0.5} placeholder="off"
                  value={cfg.anticheat?.anomaly_stddevs ?? ""}
                  onChange={(e) => setAc("anomaly_stddevs", e.target.value ? Number(e.target.value) : null)} />
                <span className="hint">Flag a score this many standard deviations better than the top-K mean (e.g. 6).</span>
              </div>
              <div className="field">
                <label htmlFor="ac-delta">Max delta / second</label>
                <input id="ac-delta" className="input mono" type="number" min={0} placeholder="off"
                  value={cfg.anticheat?.max_delta_per_sec ?? ""}
                  onChange={(e) => setAc("max_delta_per_sec", e.target.value ? Number(e.target.value) : null)} />
                <span className="hint">Flag a self-improvement faster than this (impossible jump vs prior score).</span>
              </div>
              <div className="field">
                <label htmlFor="ac-cool">Submit cooldown (sec)</label>
                <input id="ac-cool" className="input mono" type="number" min={0} placeholder="none"
                  value={cfg.anticheat?.cooldown_secs ?? ""}
                  onChange={(e) => setAc("cooldown_secs", e.target.value ? Number(e.target.value) : null)} />
                <span className="hint">Hard minimum spacing between a player's submits (on top of the rate limit).</span>
              </div>
              <div className="field">
                <label htmlFor="ac-attempts">Max attempts / season</label>
                <input id="ac-attempts" className="input mono" type="number" min={0} step={1} placeholder="unlimited"
                  value={cfg.anticheat?.max_attempts ?? ""}
                  onChange={(e) => setAc("max_attempts", e.target.value ? Number(e.target.value) : null)} />
                <span className="hint">Tournament cap: max submissions a player can make to the current version. Resets on a season reset.</span>
              </div>
              <div className="field">
                <label htmlFor="ac-hold">Hold flagged for review</label>
                <Segmented<"off" | "on">
                  value={cfg.anticheat?.hold_for_review ? "on" : "off"}
                  onChange={(v) => setAc("hold_for_review", v === "on" ? true : null)}
                  options={[
                    { value: "off", label: "Flag & count" },
                    { value: "on", label: "Hold for review" },
                  ]}
                />
                <span className="hint">When on, a flagged score is kept out of the leaderboard until you Approve it in Moderation. Needs an anomaly check above.</span>
              </div>
            </div>
          </section>

          <div className="sticky-actions">
            <Link to={isEdit ? `/app/boards/${encodeURIComponent(boardId!)}` : "/app/boards"} className="btn btn-ghost">Cancel</Link>
            <button className="btn btn-primary" disabled={busy || (!isEdit && (!idValid || !boardName.trim()))}>
              {busy ? <span className="spinner" /> : isEdit ? "Save changes" : isGroup ? `Create group (${windows.length} windows) →` : "Create leaderboard →"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/** Drop fields the backend doesn't need when aggregate isn't selected, etc. */
function normalize(cfg: LeaderboardConfig): LeaderboardConfig {
  const out = { ...cfg };
  if (out.update_method !== "aggregate") delete out.aggregation;
  return out;
}
